import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ShieldCheck, ArrowLeft, Check } from 'lucide-react'
import Footer from '@/components/Footer'
import { useLanguage } from '@/localization'
import type { CommunityData } from '@/useCommunity'

/**
 * /conduct — the full universal Code of Conduct: the canonical community-wide
 * rulebook that applies across every Uliunai game. Each game additionally has
 * its own game-specific rules on its own site; those never duplicate what's
 * here. Content is sourced from the shared `community.conduct` data.
 */
export default function ConductPage({
  conduct,
  social,
}: {
  conduct: CommunityData['conduct']
  social: CommunityData['social']
}) {
  const { t } = useLanguage()

  // Land at the top when navigating in from a teaser link.
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="flex min-h-screen flex-col">
      <main className="mx-auto w-full max-w-4xl flex-1 px-6 py-16 sm:py-20">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 font-rajdhani text-sm font-semibold text-stone-500 transition-colors hover:text-uliunai-red"
        >
          <ArrowLeft size={16} />
          {t('Back to the hub')}
        </Link>

        <header className="mt-8 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-stone-line bg-white px-4 py-1.5 font-rajdhani text-xs font-semibold uppercase tracking-[0.25em] text-uliunai-red shadow-card">
            <ShieldCheck size={16} />
            {t('The ground rules')}
          </span>
          <h1 className="metal-text mt-5 font-orbitron text-4xl font-black tracking-tight sm:text-5xl">
            {t('Code of Conduct')}
          </h1>
          <p className="mx-auto mt-5 max-w-2xl font-rajdhani text-base leading-relaxed text-stone-600">
            {t('These universal rules apply across')}{' '}
            <strong className="text-stone-900">{t('every Uliunai server')}</strong> -{' '}
            {t(
              "Killing Floor, Minecraft, and everything we host. Each game also has its own game-specific rules on its own site; those never repeat what's below. By playing on any Uliunai server you agree to follow this code.",
            )}
          </p>
        </header>

        <div className="mt-12 space-y-6">
          {conduct.map((category, i) => (
            <section
              key={category.title}
              className="rounded-2xl border border-stone-line bg-white p-7 shadow-card sm:p-8"
            >
              <div className="flex items-start gap-4">
                <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-uliunai-red/10 font-orbitron text-base font-black text-uliunai-red">
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <h2 className="font-orbitron text-xl font-bold text-stone-900 sm:text-2xl">
                    {t(category.title)}
                  </h2>
                  <p className="mt-1 font-rajdhani text-base text-stone-600">
                    {t(category.summary)}
                  </p>
                </div>
              </div>

              {category.rules && category.rules.length > 0 && (
                <ul className="mt-5 space-y-3 sm:pl-14">
                  {category.rules.map((rule) => (
                    <li key={rule} className="flex items-start gap-3 font-rajdhani text-stone-700">
                      <Check size={18} className="mt-0.5 shrink-0 text-uliunai-red" />
                      <span className="leading-relaxed">{t(rule)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <p className="mt-10 text-center font-rajdhani text-sm text-stone-500">
          {t(
            'Staff have the final say on enforcement. Breaking these rules may lead to a warning, mute, kick, or ban depending on severity.',
          )}
        </p>
      </main>

      <Footer social={social} />
    </div>
  )
}
